import { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import {
    Col, Row, Container,
} from 'react-bootstrap';
import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { PatternFormat } from 'react-number-format';
import Welcome from '../../components/Welcome';
import BlockUISpinner from '../../components/BlockUISpinner';
import {
    getDashboardNotification,
    clearDashboardNotification,
    setDashboardNotification,
    getDashboardInfoNotification,
    setDashboardInfoNotification,
    clearDashboardInfoNotification,
} from '../../storage/notification';
import NotificationMessage from '../../components/Alert/NotificationMessage';
import useHtmlTitle from '../../components/Utilities/useHtmlTitle';
import useBodyClass from '../../components/Utilities/useBodyClass';
import useAccountContext from '../../authentication/hooks';
import StandardPathway from '../../components/tiles/StandardPathway';
import {
    DashboardClient, DashboardItemDto, ProblemDetails,
    StatusEnumDto,
} from '../../api/web-api-client';
import { tokenRequest } from '../../authentication/authConfig';
import AppLogger from '../../instrumentation/AppLogger';
import getUnexpectedErrorRoute from '../common/errorRoutes';
import { HttpStatusCode } from '../../types';
import { DashBoardNotifications } from '../common/dashboardNotifications';
import SearchFilter from '../../components/SearchFilter';
import RequestItem from '../../components/RequestList/requestItem';
import NoRequests from '../../components/RequestList/noRequests';
import { InitialFilters } from '../../components/SearchFilter/types';
import usePersistedState from '../../storage/usePersistedState';

const showDashboardMessage = (message: JSX.Element | null) => (
    <>
        {message && (
            <Container>
                <Row>
                    <Col>
                        {message}
                    </Col>
                </Row>
            </Container>
        )}
    </>
);

const setNotification = () => {
    const dashboardNotification = getDashboardNotification();
    return (
        dashboardNotification
            ? (
                <NotificationMessage
                    id='notif-message-1'
                    canClose
                    onClose={clearDashboardNotification}
                    {...dashboardNotification}
                />
            )
            : null);
};

const setInfoNotification = () => {
    const dashboardInfoNotification = getDashboardInfoNotification();
    return (
        dashboardInfoNotification
            ? (
                <NotificationMessage
                    id='notif-info-message-2'
                    canClose
                    onClose={clearDashboardInfoNotification}
                    {...dashboardInfoNotification}
                />
            )
            : null);
};

const handleAlertScroll = () => {
    window.scrollTo(0, 0);
    setTimeout(() => {
        const el: HTMLElement = document.querySelector(('[id^="#notif-"]')) as HTMLElement;
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el?.focus();
    }, 100);
};

const Dashboard = () => {
    const { inProgress, accounts, instance } = useMsal();
    const [requests, setRequests] = useState<DashboardItemDto[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [reload, setReload] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const [scrollToTop, setScrollToTop] = useState(false);
    const accountContext = useAccountContext();
    const accountDetails = accountContext?.details;
    const [errorStatus, setErrorStatus] = useState({
        hasError: false, status: 0, forbidden: false, noThirdPartyAccess: false,
    });

    // eslint-disable-next-line max-len
    const [initialFilters, setInitialFilters, clearInitialFilters] = usePersistedState<InitialFilters>({ filterYearType: 'allYears', filterStatusType: 'allStatuses', filtersChanged: false }, '1');
    const dashboardMessage = setNotification();
    const dashboardInfoMessage = setInfoNotification();
    const orgName = accountContext?.details?.targetOrganisation?.targetOrganisationName as string;

    const onShowBranchSelectorClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e.preventDefault();
        accountContext?.setShowBranchSelector(true);
    };

    useEffect(() => {
        // setIsDataLoading(true); // Dashboard data
        const loadDataForDisplay = async () => {
            if (accountContext
                && accountDetails
                && accountDetails?.organisationCRMGuid) {
                if (inProgress === InteractionStatus.None && accounts.length > 0) {
                    try {
                        AppLogger.verbose('Dashboard.loadDataForDisplay', accountContext.details);
                        const client = new DashboardClient();
                        const tokenResult = await instance.acquireTokenSilent({
                            ...tokenRequest,
                            account: accounts[0],
                        });
                        client.setAuthToken(tokenResult.accessToken);
                        setErrorStatus((prevState) => ({ ...prevState, hasError: false }));
                        setIsModalOpen(
                            // List and check all Actionable modals across portal app
                            (!!accountDetails?.showBranchSelector
                            || !!accountDetails?.showRFQDeleteModal
                            || !accountDetails?.userAcceptedTermsOfUse),
                        );
                        setIsDataLoading(true);
                        const actualYear = initialFilters.filterYearType === 'allYears' ? undefined : parseInt(initialFilters.filterYearType, 10);
                        const actualStatus = initialFilters.filterStatusType === 'allStatuses' ? undefined : initialFilters.filterStatusType as StatusEnumDto;
                        // eslint-disable-next-line max-len
                        const requestsResponse = await client.getDashboardQuotesByPortalID(accountDetails.organisationCRMGuid, actualYear, actualStatus, 'descending');

                        // no longer required
                        // const requestsResponse = await client.getQuotesForDefaultOrganisation();
                        setRequests(requestsResponse);
                        setShowInfo(false);
                        // if (requestsResponse && !reload) {
                        //     const reports = requestsResponse.map((cert) => ({
                        //         reportings: cert.reportings?.filter((r) => r.status === ReportingStatus.ToDo),
                        //     })).filter(({ reportings }) => reportings?.length);
                        //     setShowInfo(reports && reports.length > 0);
                        // }
                    } catch (error) {
                        AppLogger.error('Failed to load dashboard.', error as Error);
                        const problemDetails = error as ProblemDetails;
                        if (problemDetails.status! > 0) setErrorStatus((prevState) => ({ ...prevState, status: problemDetails.status! }));
                        if (problemDetails.status === HttpStatusCode.Forbidden
                            && problemDetails.title
                            && problemDetails.title.includes('No third-party access')) {
                            setErrorStatus((prevState) => ({ ...prevState, noThirdPartyAccess: true }));
                        } else if (problemDetails.status === HttpStatusCode.Forbidden) {
                            setErrorStatus((prevState) => ({ ...prevState, forbidden: true }));
                        } else {
                            setErrorStatus((prevState) => ({ ...prevState, hasError: true }));
                        }
                        AppLogger.info('Dashboard load error values', errorStatus);
                    } finally {
                        setIsDataLoading(false);
                        setReload(false);
                    }
                }
            }
        };
        loadDataForDisplay();

        if (scrollToTop) {
            handleAlertScroll();
        }
    }, [accountContext, accountContext?.details?.organisationCRMGuid,
        accounts, instance, inProgress, reload, scrollToTop, initialFilters]);

    const renderDashboardTypeTitle = () => (
        <>
            <Row className='mb-3'>
                <Col className='d-block d-md-flex justify-content-between'>
                    <h2 className='mb-3'>Manage Testing and Calibration</h2>
                    <span className='d-block text-end'>
                        <Link
                            data-testid='new-request-button'
                            to='/services-we-offer'
                            className='btn btn-primary -d-block -d-md-flex'
                        >
                            <i className='icon-plus me-2' aria-hidden='true' role='presentation' />
                            New request
                        </Link>
                    </span>
                </Col>
            </Row>
            <Row className='mb-3'>
                <SearchFilter
                    initialFilters={initialFilters}
                    setInitialFilters={setInitialFilters}
                />
            </Row>
        </>
    );
    const showRequestList = (trequests: DashboardItemDto[], userAcceptedTermsOfUse?: boolean) => (
        <>
            {(requests !== undefined
                    && requests?.length > 0
                    && userAcceptedTermsOfUse) // Check user has accepted terms of use before showing the request list
                ? (
                    <ol className='list-unstyled'>
                        {trequests.map((r: DashboardItemDto) => (
                            <RequestItem request={r} key={r.referenceId} />
                        ))}
                    </ol>
                )
                : <NoRequests />}
        </>
    );

    useHtmlTitle('Dashboard | NMI Services portal');
    useBodyClass('dashboard');

    if (errorStatus.forbidden) {
        const message = DashBoardNotifications.getForbiddenNotification();
        setDashboardNotification(message);
    }

    if (errorStatus.noThirdPartyAccess) {
        const message = DashBoardNotifications.getThirdPartyAccessNotification(orgName);
        setDashboardNotification(message);
        if (accountContext) {
            accountContext.setTargetOrganisation(
                accountContext?.details?.abn ?? '',
                accountContext?.details?.organisation ?? '',
            );
        }
        setErrorStatus((prevState) => ({ ...prevState, noThirdPartyAccess: false }));
    }

    if (errorStatus.hasError) {
        return <Navigate to={getUnexpectedErrorRoute(errorStatus.status)} />;
    }

    if (showInfo) {
        const message = DashBoardNotifications.getReportFormsGeneratedNotification();
        setDashboardInfoNotification(message);
    } else {
        clearDashboardInfoNotification();
    }

    return (
        <>
            {isLoading && (
                <BlockUISpinner>
                    <p>Loading...</p>
                </BlockUISpinner>
            )}
            <div aria-busy={isLoading} aria-live={isModalOpen ? undefined : 'polite'}>
                <Welcome />
            </div>
            {showDashboardMessage(dashboardMessage)}
            {showDashboardMessage(dashboardInfoMessage)}
            <Container>
                <Row className='mb-5'>
                    <Col aria-busy={isLoading} aria-live={isModalOpen ? undefined : 'polite'}>
                        {!!accountContext?.details && (
                            <>
                                <h2 className='h1 text-body mb-3' aria-describedby='orgABN'>
                                    <span className='d-block mb-1 fs-5 fw-normal'>
                                        {'Currently managing '}
                                    </span>
                                    <span className='d-block text-break'>
                                        {!!(accountContext?.details?.organisation) && (`${accountContext?.details?.organisation}`)}
                                    </span>
                                    <span className='d-block text-break'>
                                        {!!(accountContext?.details?.trading) && (`${accountContext?.details?.trading}`)}
                                        {!!(accountContext?.details?.branch) && (` - ${accountContext?.details?.branch}`)}
                                    </span>
                                </h2>
                                {!!(accountContext?.details?.abn) && (
                                    <>
                                        <span id='orgABN' className='d-inline-block me-3 fw-normal'>
                                            {'ABN: '}
                                            <PatternFormat
                                                value={accountContext?.details?.abn}
                                                displayType='text'
                                                format='## ### ### ###'
                                                aria-hidden='true'
                                                role='presentation'
                                                valueIsNumericString={false}
                                            />
                                        </span>
                                        <span className='visually-hidden'>
                                            {accountContext?.details?.abn?.split('').join(' ')}
                                        </span>
                                    </>
                                )}
                                <Link
                                    data-testid='open-manage-branch-division-button'
                                    role='button'
                                    onClick={onShowBranchSelectorClick}
                                    to='#0'
                                    className='text-nowrap'
                                >
                                    Add or manage branch/location
                                </Link>
                            </>
                        )}
                    </Col>
                </Row>
                {renderDashboardTypeTitle()}
                <Row className='mb-4'>
                    <Col
                        {...{ inert: isModalOpen ? '' : undefined }}
                        aria-busy={!isModalOpen && isDataLoading}
                        aria-live={isModalOpen ? undefined : 'polite'}
                    >
                        {!isModalOpen && isDataLoading
                            ? (
                                <BlockUISpinner partial>
                                    <p>Loading data...</p>
                                </BlockUISpinner>
                            )
                            : (
                                // <div className='dashboard-items'>
                                //     <RequestList requests={requests} />
                                // </div>
                                <div className='dashboard-items'>
                                    {showRequestList(requests, accountDetails?.userAcceptedTermsOfUse)}
                                </div>
                            )}
                    </Col>
                </Row>

                <Row className='mb-5'>
                    <h2 className='pt-md-4 mb-3'>Quick links</h2>
                    <Col md>
                        <StandardPathway
                            type='internal'
                            title='Services we offer'
                            linkDescription=' '
                            bodyText='The National Measurement Institute (NMI) offers a range of service. Find out more...'
                            to='/services-we-offer'
                        />
                    </Col>
                    <Col md>
                        <StandardPathway
                            type='internal'
                            title='Help guide'
                            linkDescription=' '
                            bodyText='View help guides for common issues and troubleshooting tips'
                            to='/help-guide'
                        />
                    </Col>
                    <Col md>
                        {/* <StandardPathway
                            type='external'
                            title='Get help'
                            linkDescription=' '
                            bodyText='See a list of registered consultants and contact National Measurement Institute (NMI)'
                            linkHref='https://www.measurement.gov.au'
                            target='_blank'
                        /> */}
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default Dashboard;
